import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy } from "lucide-react";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { ApiClientCreatedDto } from "@/features/admin/api/dto";
import { useCreateApiClient } from "@/features/admin/api/use-create-api-client";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createApiClientSchema,
  parseScopesInput,
  type ApiClientFormValues,
} from "@/features/admin/schemas/api-client.schema";

const EMPTY_VALUES: ApiClientFormValues = {
  name: "",
  rateLimitPerMinute: 120,
  scopes: "",
};

/**
 * Création d'un client API partenaire (`POST /ApiClients/add`, §6.1 —
 * endpoint gateway). Preview obligatoire avant submit (règle projet), puis
 * dialog affichant la `apiKey` — retournée UNE SEULE FOIS par le serveur.
 */
export function ApiClientFormDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createApiClientSchema(), [i18n.language]);

  const create = useCreateApiClient();
  const [preview, setPreview] = useState<ApiClientFormValues | null>(null);
  const [created, setCreated] = useState<ApiClientCreatedDto | null>(null);
  const [copied, setCopied] = useState(false);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(parsed.data);
    },
  });

  const handleConfirm = () => {
    if (!preview) return;
    create.mutate(
      {
        name: preview.name.trim(),
        rateLimitPerMinute: preview.rateLimitPerMinute,
        scopes: parseScopesInput(preview.scopes),
      },
      {
        onSuccess: (data) => {
          notifSilence(t("admin.apiClients.feedback.created"));
          setPreview(null);
          setCreated(data);
          onOpenChange(false);
          form.reset();
        },
        onError: (error) => notifSilenceError(error.message),
      }
    );
  };

  const copyApiKey = async () => {
    if (!created?.apiKey) return;
    try {
      await navigator.clipboard.writeText(created.apiKey);
      setCopied(true);
      notifSilence(t("admin.apiClients.created.copied"));
    } catch {
      notifSilenceError(t("error"));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("admin.apiClients.create")}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field
              name="name"
              validators={{
                onChange: ({ value }) =>
                  schema.shape.name.safeParse(value ?? "").success
                    ? undefined
                    : schema.shape.name.safeParse(value ?? "").error?.issues[0]
                        ?.message,
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="client-name">
                    {t("admin.apiClients.form.name")}
                  </Label>
                  <Input
                    id="client-name"
                    placeholder={t("admin.apiClients.form.namePlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="rateLimitPerMinute"
              validators={{
                onChange: ({ value }) =>
                  schema.shape.rateLimitPerMinute.safeParse(value).success
                    ? undefined
                    : schema.shape.rateLimitPerMinute.safeParse(value)
                        .error?.issues[0]?.message,
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="client-rate-limit">
                    {t("admin.apiClients.form.rateLimit")}
                  </Label>
                  <Input
                    id="client-rate-limit"
                    type="number"
                    min={1}
                    className="notranslate"
                    placeholder={t("admin.apiClients.form.rateLimitPlaceholder")}
                    value={field.state.value ?? ""}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.valueAsNumber)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="scopes"
              validators={{
                onChange: ({ value }) =>
                  schema.shape.scopes.safeParse(value ?? "").success
                    ? undefined
                    : schema.shape.scopes.safeParse(value ?? "").error?.issues[0]
                        ?.message,
              }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="client-scopes">
                    {t("admin.apiClients.form.scopes")}
                  </Label>
                  <Textarea
                    id="client-scopes"
                    className="font-mono notranslate"
                    placeholder={t("admin.apiClients.form.scopesPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <ButtonSpinner /> : null}
                {t("common.confirm")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {preview ? (
        <ShareablePreview
          data={{
            name: preview.name.trim(),
            rateLimitPerMinute: preview.rateLimitPerMinute,
            scopes: parseScopesInput(preview.scopes),
          }}
          title={t("admin.apiClients.preview.createTitle")}
          description={t("admin.apiClients.preview.createDescription")}
          mode="user"
          defaultOpen
          onOpenChange={(isOpen) => {
            if (!isOpen && !create.isPending) setPreview(null);
          }}
          maxHeight="90vh"
          actions={createPreviewActions({
            onConfirm: handleConfirm,
            onCancel: () => setPreview(null),
            t,
            confirmLoading: create.isPending,
            withIndicatorOnConfirm: true,
          })}
        />
      ) : null}

      <Dialog
        open={created !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setCreated(null);
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("admin.apiClients.created.title")}</DialogTitle>
            <DialogDescription>
              {t("admin.apiClients.created.description")}
            </DialogDescription>
          </DialogHeader>
          {created ? (
            <div className="flex items-center gap-2">
              <NoTranslate
                as="span"
                className="flex-1 break-all rounded-md bg-muted p-3 font-mono text-xs"
              >
                {created.apiKey}
              </NoTranslate>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => void copyApiKey()}
                aria-label={t("admin.apiClients.created.copy")}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-success" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" onClick={() => setCreated(null)}>
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
